import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { findAncestor } from 'utils/dom_utils';
import { handleKeyEvents } from 'utils/handlers';

import Menu from 'components/shared/Menu';

import type { CollectionName, NamedCollection, UncollectedCollection } from 'domains/photos';

export type CollectionsMenuProps = {
  onSaveUncollected: () => void;
  onSelectCollection: (id: string | null) => void;
  onShowCollectionsManager: () => void;
  collections: NamedCollection[];
  uncollected: UncollectedCollection;
  activeCollection: CollectionName;
};

function CollectionsMenu({
  onSaveUncollected,
  onSelectCollection,
  onShowCollectionsManager,
  collections,
  uncollected,
  activeCollection,
}: CollectionsMenuProps) {
  const collectionsMenuSelectNext = (current: EventTarget & HTMLButtonElement) => {
    const menu = findAncestor(current, 'ul');
    if (!menu) return;

    const menuLinks = menu.querySelectorAll<HTMLButtonElement>('.collections_menu_link');
    const currentIndex = Array.from(menuLinks).findIndex((m) => m === current);
    const newIndex = currentIndex + 1;
    if (newIndex >= menuLinks.length) {
      const trigger = document.querySelector<HTMLButtonElement>('#collections-menu-trigger');
      if (trigger) {
        trigger.focus();
      }
    } else {
      menuLinks[newIndex].focus();
      menuLinks[newIndex].scrollIntoView();
    }
  };

  const collectionsMenuSelectPrevious = (current: EventTarget & HTMLButtonElement) => {
    const menu = findAncestor(current, 'ul');
    if (!menu) return;

    const menuLinks = menu.querySelectorAll<HTMLButtonElement>('.collections_menu_link');
    const currentIndex = Array.from(menuLinks).findIndex((m) => m === current);
    const newIndex = currentIndex - 1 <= -1 ? 0 : currentIndex - 1;
    menuLinks[newIndex].focus();
    menuLinks[newIndex].scrollIntoView();
  };

  function handleCollectionsMenuNavigate(e: React.KeyboardEvent<HTMLButtonElement>) {
    const { key, currentTarget } = e;
    e.preventDefault();
    const trigger = document.querySelector<HTMLButtonElement>('#collections-menu-trigger');

    switch (key) {
      case 'ArrowUp':
        collectionsMenuSelectPrevious(currentTarget);
        break;
      case 'ArrowDown':
        collectionsMenuSelectNext(currentTarget);
        break;
      case 'ArrowRight':
        currentTarget.click();
        if (trigger) {
          trigger.click();
          trigger.focus();
        }
        break;
      default:
        e.preventDefault();
    }
  }

  const onlyUncollected = collections.length === 0;
  const noImages = onlyUncollected && uncollected.count === 0;

  const menuText = useMemo(() => {
    const collectionText = onlyUncollected
      ? 'Save images as a Collection'
      : activeCollection || 'Uncollected';
    return noImages ? 'Add images to begin' : collectionText;
  }, [activeCollection, noImages, onlyUncollected]);

  const collectionsMenuItems = useMemo(() => {
    const menuItems = collections
      .sort((a, b) => (a.name > b.name ? 1 : -1))
      .map((collection) => (
        <button
          key={collection.name}
          className="collections_menu_link"
          role="menuitem"
          data-id={collection.name}
          onClick={() => onSelectCollection(collection.name)}
          onKeyDown={handleKeyEvents({
            open: () => onSelectCollection(collection.name),
            navigate: handleCollectionsMenuNavigate,
            close: () => null,
          })}
        >
          {collection.name}
          <FontAwesomeIcon icon="image" size="lg" />
          <div className="count-bubble">{collection.count}</div>
        </button>
      ));

    // Put the uncollected item first in menu
    if (uncollected.count) {
      menuItems.push(
        <button
          className="collections_menu_link"
          role="menuitem"
          onClick={() => onSelectCollection(null)}
          onKeyDown={handleKeyEvents({
            open: () => onSelectCollection(null),
            navigate: handleCollectionsMenuNavigate,
            close: () => null,
          })}
        >
          Uncollected
          <FontAwesomeIcon icon="image" size="lg" />
          <div className="count-bubble">{uncollected.count}</div>
        </button>
      );
    }

    // Add the manage button
    menuItems.push(
      <button
        role="menuitem"
        className="collections_menu_link admin-link manager-trigger"
        onClick={onShowCollectionsManager}
        onKeyDown={handleKeyEvents({
          open: onShowCollectionsManager,
          navigate: handleCollectionsMenuNavigate,
          close: () => null,
        })}
      >
        View / Manage Collections
        <FontAwesomeIcon icon={['far', 'copy']} size="lg" />
      </button>
    );

    // Add the save uncollected button if needed
    if (activeCollection === null && uncollected.count > 0) {
      menuItems.push(
        <button
          role="menuitem"
          className="collections_menu_link admin-link save-trigger"
          onClick={onSaveUncollected}
          onKeyDown={handleKeyEvents({
            open: onSaveUncollected,
            navigate: handleCollectionsMenuNavigate,
            close: () => null,
          })}
        >
          Save as Collection
        </button>
      );
    }

    return menuItems;
  }, [activeCollection, collections, uncollected]);

  return (
    <Menu
      menuItems={collectionsMenuItems}
      triggerText={menuText}
      triggerId="collections-menu-trigger"
      direction="up"
      position="right"
      appearance="themed"
    />
  );
}

export default CollectionsMenu;
